
import React, { useMemo, useEffect } from 'react';
import { CalendarTable } from './calendar/CalendarTable';
import { CalendarLegend } from './calendar/CalendarLegend';
import { calculateMonthData, getVisibilityRating } from '@/utils/calendarData';
import BestNights from '@/components/BestNights';
import { useIsMobile } from '@/hooks/use-mobile';
import { FreeUserHeader } from './calendar/FreeUserHeader';
import { useAuth } from '@/contexts/AuthContext';
import { HighLatitudeBanner } from './calendar/HighLatitudeBanner';
import { MapPin } from 'lucide-react';

interface LocationData {
  name: string;
  latitude: number;
  longitude: number;
}

interface MilkyWayCalendarProps {
  location: LocationData;
  month: number;
  year: number;
  isYearlyView?: boolean;
  isMarketingPreview?: boolean;
  onViewChange?: () => void;
  forceShowHeader?: boolean;
  selectedDay?: number;
  onDaySelect?: (dayDate: number) => void;
}

export const MilkyWayCalendar: React.FC<MilkyWayCalendarProps> = ({
  location,
  month,
  year,
  isYearlyView = false,
  isMarketingPreview = false,
  onViewChange,
  forceShowHeader = false,
  selectedDay,
  onDaySelect,
}) => {
  const isMobile = useIsMobile();
  const { user, isSubscribed } = useAuth();
  
  const rawMonthData = useMemo(() => {
    try {
      return calculateMonthData(location, month, year);
    } catch (error) {
      console.error('Error calculating month data:', error);
      return [];
    }
  }, [location, month, year]);

  const monthData = useMemo(() => {
    if (isMarketingPreview) {
      return rawMonthData.filter(day => getVisibilityRating(day) >= 5);
    }
    return rawMonthData;
  }, [rawMonthData, isMarketingPreview]);

  // Trigger view change callback when location/month/year changes
  useEffect(() => {
    if (onViewChange && monthData.length > 0) {
      onViewChange();
    }
  }, [location.name, month, year]); // eslint-disable-line react-hooks/exhaustive-deps

  // Show header for free users (not subscribed), non-logged-in users, or when forced (shared plans)
  const showFreeUserHeader = !isYearlyView && (forceShowHeader || !user || !isSubscribed);

  // Detect why all days this month have zero visibility (always check raw data)
  const allZeroVisibility = rawMonthData.length > 0 && rawMonthData.every(day => getVisibilityRating(day) === 0);
  const hasNoAstroDarkness = rawMonthData.some(day => day.astroTwilightEvening === '--:--' || day.astroTwilightMorning === '--:--');
  const noVisibilityReason = !allZeroVisibility ? null
    : hasNoAstroDarkness ? 'twilight' as const
    : rawMonthData.every(day => day.galacticCoreStart === 'Below horizon') ? 'below_horizon' as const
    : 'not_visible' as const;

  if (monthData.length === 0) {
    console.error('No month data available or no good visibility days');
    return (
      <div className="text-center p-8">
        {noVisibilityReason && <HighLatitudeBanner latitude={location.latitude} month={month} reason={noVisibilityReason} />}
        <p className="text-muted-foreground mb-2">
          {isMarketingPreview
            ? "No nights with good visibility (rating 5+) this month"
            : "Unable to load calendar data. Please try again."
          }
        </p>
        {isMarketingPreview && (
          <p className="text-sm text-muted-foreground">
            Consider selecting a different month for better Milky Way visibility.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`space-y-${isMobile ? '6' : '8'}`}>
      {/* No-visibility notice */}
      {noVisibilityReason && (
        <HighLatitudeBanner latitude={location.latitude} month={month} reason={noVisibilityReason} />
      )}

      {/* Header for free/non-logged-in users */}
      {showFreeUserHeader && (
        <FreeUserHeader 
          location={location}
          month={month}
          year={year}
          monthData={monthData}
        />
      )}

      {/* Best Nights Section - Only show for non-yearly view */}
      {!isYearlyView && (
        <BestNights
          monthData={monthData}
          location={location}
          month={month}
          year={year}
          selectedDay={selectedDay}
          onDaySelect={onDaySelect}
        />
      )}

      {/* Calendar Table */}
      {onDaySelect && (
        <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-gold/5 border border-gold/20">
          <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
          <p className="text-sm text-foreground/80">
            Click any date to see where the galactic core will be on the map
          </p>
        </div>
      )}
      <CalendarTable monthData={monthData} selectedDay={selectedDay} onDaySelect={onDaySelect} />

      {/* Legend - Simplified for yearly view */}
      {!isYearlyView && <CalendarLegend />}
    </div>
  );
};
